import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";

interface Hit {
  plate: string;
  brand: string | null;
  vehicleTypeName: string | null;
  isTrailer: boolean;
  trailerPlate: string | null;
  status: string;
}

/** Mesma normalização do servidor: maiúscula, só letras e números. */
function normalize(s: string): string {
  return s.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

/**
 * Campo "Buscar placa" do menu Frota. Digitou, sugere do cadastro; Enter ou
 * clique abre o raio-X da placa. Enter com algo digitado e nenhuma sugestão
 * escolhida abre o raio-X do que foi digitado mesmo — o raio-X sabe dizer
 * quando a placa não existe.
 */
export function PlateSearch() {
  const navigate = useNavigate();
  const [texto, setTexto] = useState("");
  const [aberto, setAberto] = useState(false);
  const [ativo, setAtivo] = useState(0);
  const [debounced, setDebounced] = useState("");
  const raiz = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(normalize(texto)), 180);
    return () => clearTimeout(t);
  }, [texto]);

  useEffect(() => {
    const fora = (e: MouseEvent) => {
      if (raiz.current && !raiz.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, []);

  const { data } = useQuery<{ items: Hit[] }>({
    queryKey: ["plates", "search", debounced],
    queryFn: () => api.get(`/dashboards/transport/plates?q=${encodeURIComponent(debounced)}`),
    enabled: debounced.length >= 2,
    staleTime: 60_000,
  });
  const hits = debounced.length >= 2 ? (data?.items ?? []) : [];

  const abrir = (plate: string) => {
    const p = normalize(plate);
    if (!p) return;
    setAberto(false);
    setTexto("");
    navigate(`/frota/placa/${encodeURIComponent(p)}`);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setAtivo((i) => Math.min(i + 1, Math.max(hits.length - 1, 0))); setAberto(true); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setAtivo((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); abrir(hits[ativo]?.plate ?? texto); }
    else if (e.key === "Escape") { setAberto(false); }
  };

  return (
    <div ref={raiz} className="relative px-3 mb-2">
      <input
        type="text"
        value={texto}
        onChange={(e) => { setTexto(e.target.value); setAtivo(0); setAberto(true); }}
        onFocus={() => setAberto(true)}
        onKeyDown={onKeyDown}
        placeholder="Buscar placa…"
        aria-label="Buscar placa"
        autoComplete="off"
        spellCheck={false}
        className="w-full rounded-lg bg-white/10 border border-white/10 px-3 py-1.5 text-sm text-white placeholder:text-tm-100/40 focus:outline-none focus:ring-2 focus:ring-white/30 uppercase tracking-wide"
      />
      {aberto && debounced.length >= 2 && (
        <div className="absolute left-3 right-3 mt-1 z-30 rounded-lg bg-white shadow-lg border border-gray-200 overflow-hidden">
          {hits.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-500">
              Nenhuma placa com "{debounced}". Enter abre o raio-X assim mesmo.
            </div>
          ) : (
            <ul className="max-h-72 overflow-y-auto">
              {hits.map((h, i) => (
                <li key={h.plate}>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => abrir(h.plate)}
                    onMouseEnter={() => setAtivo(i)}
                    className={`w-full text-left px-3 py-2 flex items-center justify-between gap-2 ${i === ativo ? "bg-tm-50" : "bg-white"}`}
                  >
                    <span className="text-sm font-medium text-gray-900">{h.plate}</span>
                    <span className="text-xs text-gray-500 truncate">
                      {h.isTrailer ? "carreta" : (h.vehicleTypeName ?? h.brand ?? "")}
                      {!h.isTrailer && h.trailerPlate ? ` · ${h.trailerPlate}` : ""}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
